import React from 'react';
import { Box, Card, CardContent, Chip, Typography, styled } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import { PerkTemplate } from './models/PerkTemplate';
import { PerkPreviewThumbnail } from './PerkPreviewThumbnail';
import { awardTemplateLabels } from 'components/customers/models/Award';
import { MuiChip } from 'ui/MuiChip';

const PREFIX = 'PerkTemplateCardBase';

const classes = {
    description: `${PREFIX}-description`
};

interface StyledCardProps {
    clickable?: boolean;
}

const StyledCard = styled(Card, {
    shouldForwardProp: prop => prop !== 'clickable'
})<StyledCardProps>(({ clickable }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    ...(clickable && {
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)'
        }
    }),
    [`& .${classes.description}`]: {
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden'
    }
}));

interface PerkTemplateCardBaseProps {
    template: PerkTemplate;
    suggested?: boolean;
    thumbnailHeight?: number;
    onClick?: () => void;
    children?: React.ReactNode;
}

export const PerkTemplateCardBase: React.FC<PerkTemplateCardBaseProps> = ({
    template,
    suggested,
    thumbnailHeight = 180,
    onClick,
    children
}) => {
    const typeLabel = awardTemplateLabels[template.perk.template] || template.perk.template;

    return (
        <StyledCard variant="outlined" clickable={!!onClick} onClick={onClick}>
            <Box sx={{ height: thumbnailHeight, position: 'relative' }}>
                {suggested && (
                    <MuiChip
                        icon={<AutoAwesome />}
                        label="Suggested"
                        color="purple"
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1
                        }}
                    />
                )}
                <PerkPreviewThumbnail
                    backgroundImageUrl={template.perk.visualisation?.backgroundImageUrl}
                    backgroundOverlayColour={template.perk.visualisation?.backgroundOverlayColour}
                    backgroundOverlayOpacity={template.perk.visualisation?.backgroundOverlayOpacity}
                />
            </Box>
            <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {template.name}
                </Typography>
                <Typography
                    variant="body2"
                    color="textSecondary"
                    className={classes.description}
                    gutterBottom
                >
                    {template.description}
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                    {typeLabel && <Chip label={typeLabel} size="small" variant="outlined" />}
                    {template.perk.token && (
                        <Chip label={template.perk.token} size="small" variant="outlined" />
                    )}
                </Box>
            </CardContent>
            {children}
        </StyledCard>
    );
};
